/**
 * Feature 14 — Admin queue for claim requests.
 *
 * Lists pending claims, lets the admin Approve / Deny / Request more info,
 * and then run the Setup Organization step which creates the Organization,
 * the claimant User, and mails the activation link.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import PageHeader from '../../components/PageHeader';
import LoadingSpinner from '../../components/LoadingSpinner';
import { directoryApi } from '../../services/directory';
import type { ClaimRequestAdmin } from '../../types';


type Action = 'approve' | 'deny' | 'request-info' | 'setup-org';


function StatusBadge({ status }: { status: ClaimRequestAdmin['status'] }) {
  const map: Record<string, string> = {
    pending: 'bg-amber-50 text-amber-700 border-amber-200',
    approved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    denied: 'bg-red-50 text-red-700 border-red-200',
    more_info_requested: 'bg-slate-100 text-slate-700 border-slate-200',
  };
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${map[status]}`}>
      {status.replace(/_/g, ' ')}
    </span>
  );
}


function ClaimDetail({
  claim, onClose,
}: { claim: ClaimRequestAdmin; onClose: () => void }) {
  const qc = useQueryClient();
  const [notes, setNotes] = useState(claim.admin_notes || '');
  const [orgName, setOrgName] = useState(claim.public_company_name);

  const action = useMutation({
    mutationFn: (act: Action) => {
      if (act === 'approve') return directoryApi.adminApproveClaim(claim.id, notes);
      if (act === 'deny') return directoryApi.adminDenyClaim(claim.id, notes);
      if (act === 'request-info') return directoryApi.adminRequestInfoClaim(claim.id, notes);
      return directoryApi.adminSetupOrgForClaim(claim.id, { org_name: orgName });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'claims'] });
      onClose();
    },
  });

  const isPending = claim.status === 'pending' || claim.status === 'more_info_requested';
  const canSetupOrg = claim.status === 'approved';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl max-w-xl w-full p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-lg font-semibold text-slate-900">
          {claim.public_company_name}
        </h2>
        <p className="text-xs text-slate-500">
          {claim.public_company_city}, {claim.public_company_state} —{' '}
          <StatusBadge status={claim.status} />
        </p>
        <dl className="mt-4 space-y-3 text-sm">
          <div>
            <dt className="text-xs text-slate-400">Claimant</dt>
            <dd className="text-slate-800">
              {claim.claimant_name} ({claim.claimant_email})
            </dd>
          </div>
          {claim.claimant_title && (
            <div>
              <dt className="text-xs text-slate-400">Title</dt>
              <dd className="text-slate-800">{claim.claimant_title}</dd>
            </div>
          )}
          {claim.justification && (
            <div>
              <dt className="text-xs text-slate-400">Justification</dt>
              <dd className="text-slate-700 whitespace-pre-wrap">
                {claim.justification}
              </dd>
            </div>
          )}
        </dl>

        <div className="mt-4">
          <span className="text-xs font-medium text-slate-600">
            Admin notes
          </span>
          <textarea
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="mt-1 w-full rounded-lg border-2 border-slate-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
            placeholder="Required for Deny / Request more info"
          />
        </div>

        {action.isError && (
          <p className="mt-3 text-xs text-red-600">
            {(action.error as Error)?.message || 'Action failed.'}
          </p>
        )}

        {isPending && (
          <div className="mt-4 flex flex-wrap gap-2">
            <button
              onClick={() => action.mutate('approve')}
              disabled={action.isPending}
              className="rounded-xl bg-emerald-500 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50 hover:bg-emerald-600"
            >
              Approve
            </button>
            <button
              onClick={() => action.mutate('deny')}
              disabled={action.isPending || !notes.trim()}
              className="rounded-xl bg-red-500 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50 hover:bg-red-600"
            >
              Deny
            </button>
            <button
              onClick={() => action.mutate('request-info')}
              disabled={action.isPending || !notes.trim()}
              className="rounded-xl bg-amber-500 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50 hover:bg-amber-600"
            >
              Request more info
            </button>
          </div>
        )}

        {canSetupOrg && (
          <div className="mt-6 rounded-xl border border-slate-200 bg-slate-50 p-4">
            <div className="text-sm font-semibold text-slate-900 mb-2">
              Set up Organization
            </div>
            <p className="text-xs text-slate-500 mb-3">
              Creates the vendor Organization, the claimant User, and mails
              a 7-day activation link.
            </p>
            <label className="block mb-3">
              <span className="text-xs font-medium text-slate-600">Organization name</span>
              <input
                value={orgName}
                onChange={(e) => setOrgName(e.target.value)}
                className="mt-1 w-full rounded-lg border-2 border-slate-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
              />
            </label>
            <button
              onClick={() => action.mutate('setup-org')}
              disabled={action.isPending}
              className="rounded-xl bg-brand-500 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50 hover:bg-brand-600"
            >
              {action.isPending ? 'Setting up…' : 'Set up & send activation'}
            </button>
          </div>
        )}

        <div className="mt-6 flex justify-end">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium rounded-lg text-slate-700 hover:bg-slate-100">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}


export default function AdminClaims() {
  const [statusFilter, setStatusFilter] = useState('pending');
  const [page, setPage] = useState(1);
  const [activeClaim, setActiveClaim] = useState<ClaimRequestAdmin | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'claims', page, statusFilter],
    queryFn: () => directoryApi.adminListClaims({
      page: String(page),
      status: statusFilter || undefined,
    }),
  });

  const totalPages = data ? Math.ceil(data.count / 20) : 1;

  return (
    <>
      <PageHeader title="Claim Requests" description="Directory claims awaiting review" />

      <div className="mb-4">
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          className="rounded-xl border-2 border-slate-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
        >
          <option value="">All</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="denied">Denied</option>
          <option value="more_info_requested">More info requested</option>
        </select>
      </div>

      <div className="rounded-2xl border border-slate-100 bg-white overflow-hidden">
        {isLoading ? (
          <div className="p-12 flex justify-center"><LoadingSpinner size="md" /></div>
        ) : !data || data.results.length === 0 ? (
          <div className="p-12 text-center text-sm text-slate-500">No claims.</div>
        ) : (
          <table className="w-full">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3 text-left font-medium">Company</th>
                <th className="px-4 py-3 text-left font-medium">Claimant</th>
                <th className="px-4 py-3 text-left font-medium">Status</th>
                <th className="px-4 py-3 text-left font-medium">Submitted</th>
              </tr>
            </thead>
            <tbody>
              {data.results.map((c) => (
                <tr
                  key={c.id}
                  className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                  onClick={() => setActiveClaim(c)}
                >
                  <td className="px-4 py-3 text-sm text-slate-800">
                    {c.public_company_name} ({c.public_company_state})
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-700">
                    {c.claimant_name}
                  </td>
                  <td className="px-4 py-3"><StatusBadge status={c.status} /></td>
                  <td className="px-4 py-3 text-xs text-slate-500">
                    {new Date(c.created_at).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {data && totalPages > 1 && (
        <div className="mt-4 flex items-center justify-end gap-2 text-sm">
          <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} className="rounded-lg border border-slate-200 px-3 py-1 disabled:opacity-50">Previous</button>
          <span className="text-slate-500">Page {page} of {totalPages}</span>
          <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="rounded-lg border border-slate-200 px-3 py-1 disabled:opacity-50">Next</button>
        </div>
      )}

      {activeClaim && (
        <ClaimDetail claim={activeClaim} onClose={() => setActiveClaim(null)} />
      )}
    </>
  );
}
